import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { uploadSkusToCampaignCSV } from 'services/api/admin-csv';
import { postSkusToCampaign } from 'services/api/admin-campaigns';
import {
  countProducts,
  formatSkus,
  formatCsvData,
  formatError,
} from 'utils/admin-csv';
import BaseModal from 'shared-components/BaseModal/Modal';
import CloseButton from 'shared-components/CloseButton/CloseButton';
import AddSkusScreen from './AddSkusScreen';
import LoadingScreen from './LoadingScreen';
import styles from './CSVModal.module.css';

const AddProductsToCampaignModal = ({
  campaign,
  onClickClose,
  onUploadSuccess,
  updateData,
  type,
}) => {
  const [confirmationScreen, setConfirmationScreen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isAddingSkus, setIsAddingSkus] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [productsNumber, setProductsNumber] = useState(0);
  const [skus, setSkus] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (confirmationScreen) {
      const getProductsNumber = async () => {
        const num = await countProducts(selectedFile || skus);
        setProductsNumber(num);
      };
      getProductsNumber();
    }
  }, [confirmationScreen]);

  const handleSelectFile = (event) => setSelectedFile(event.target.files[0]);

  const handleChangeConfirm = () => setConfirmed((prevState) => !prevState);

  const handleChangeSkus = (event) => setSkus(event.target.value);

  const handleClickNext = () => {
    setConfirmationScreen(true);
    if (skus) setIsAddingSkus(true);
  };

  const clearState = () => {
    setSelectedFile(null);
    setSkus('');
    setProductsNumber(0);
    setConfirmationScreen(false);
    setIsAddingSkus(false);
    setConfirmed(false);
    setUploading(false);
    setError(null);
  };

  const handleClose = () => {
    clearState();
    onClickClose();
  };

  const handleSuccess = async (uploadingFile, response) => {
    const { message, value } = response;
    const num = await countProducts(selectedFile || skus);
    const toastTitle = uploadingFile
      ? 'CSV successfully uploaded.'
      : `${num === 1 ? 'Product' : 'Products'} successfully uploaded.`;
    setProductsNumber(num);
    onUploadSuccess({ title: toastTitle, message, value });
    clearState();
    onClickClose();
    updateData();
  };

  const handleUnsuccess = (err) => {
    setUploading(false);
    setError(err);
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      const uploadingFile = !!selectedFile;
      const request = uploadingFile
        ? uploadSkusToCampaignCSV
        : postSkusToCampaign;
      const data = uploadingFile
        ? formatCsvData(selectedFile)
        : formatSkus(skus);
      const response = await request(campaign.id, data);
      if (response.success) {
        handleSuccess(uploadingFile, response);
      } else {
        // status code 200 and response.success = false
        const err = {
          value: response.value,
          message: 'All skus successfully updated except for the following:',
        };
        handleUnsuccess(err);
      }
    } catch (e) {
      const err = formatError(selectedFile, e);
      handleUnsuccess(err);
    }
  };

  if (uploading) return <LoadingScreen type={type} />;

  return (
    <BaseModal
      isOpen={!!type}
      onRequestClose={handleClose}
      className={styles.modal}
    >
      <div className={styles.container}>
        <CloseButton onClick={handleClose} />
        <AddSkusScreen
          confirmationScreen={confirmationScreen}
          selectedFile={selectedFile}
          skus={skus}
          isAddingSkus={isAddingSkus}
          confirmed={confirmed}
          productsNumber={productsNumber}
          campaign={campaign}
          error={error}
          onSelectFile={handleSelectFile}
          onClearState={clearState}
          onChangeSkus={handleChangeSkus}
          onChangeConfirm={handleChangeConfirm}
          onClose={handleClose}
          onClickNext={handleClickNext}
          onSubmit={handleSubmit}
        />
      </div>
    </BaseModal>
  );
};

AddProductsToCampaignModal.propTypes = {
  campaign: PropTypes.shape({ id: PropTypes.number }).isRequired,
  onClickClose: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
  updateData: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

export default AddProductsToCampaignModal;
